/**
 * Fly curve generation algorithm
 *
 * Generates smooth random bezier curves inside a circle for the fly cast minigame.
 * Curves resemble a fly line unfurling — wide sweeping S-curves with C1 continuity.
 */

import type { Point2D, FlyCurve, FlyCurveSegment } from '@/types';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';

// ============================================================
// Math helpers
// ============================================================

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpPoint(a: Point2D, b: Point2D, t: number): Point2D {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
}

function distance(a: Point2D, b: Point2D): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function pointOnCircle(cx: number, cy: number, radius: number, angle: number): Point2D {
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
}

/**
 * Evaluate a cubic bezier at parameter t (0-1)
 */
function evalCubicBezier(
  p0: Point2D,
  cp1: Point2D,
  cp2: Point2D,
  p3: Point2D,
  t: number,
): Point2D {
  const u = 1 - t;
  const uu = u * u;
  const uuu = uu * u;
  const tt = t * t;
  const ttt = tt * t;

  return {
    x: uuu * p0.x + 3 * uu * t * cp1.x + 3 * u * tt * cp2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * cp1.y + 3 * u * tt * cp2.y + ttt * p3.y,
  };
}

// ============================================================
// Curve generation
// ============================================================

/**
 * Generate a random smooth bezier curve inside a circle.
 *
 * The curve flows from a start point on the circle edge to an end point
 * on the opposite side, passing through 1-3 interior waypoints. Control
 * handles create wide sweeping arcs with C1 continuity at each joint.
 *
 * @param circleRadius - Radius of the bounding circle (default from BALANCE_CONFIG)
 * @param cx - Circle center X (default: circleRadius + padding)
 * @param cy - Circle center Y (default: circleRadius + padding)
 * @returns A FlyCurve ready for rendering and trace comparison
 */
export function generateFlyCurve(
  circleRadius: number = BALANCE_CONFIG.FLY_CAST_CIRCLE_RADIUS,
  cx: number = circleRadius,
  cy: number = circleRadius,
): FlyCurve {
  // Retry up to 5 times if we get a degenerate curve
  for (let attempt = 0; attempt < 5; attempt++) {
    const curve = tryGenerateCurve(circleRadius, cx, cy);
    if (curve.totalLength > circleRadius * 1.2) {
      return curve;
    }
  }
  // Accept whatever we get on the last try
  return tryGenerateCurve(circleRadius, cx, cy);
}

function tryGenerateCurve(radius: number, cx: number, cy: number): FlyCurve {
  // 1. Pick start and end on circle edge (120-240 degrees apart for variety)
  const startAngle = Math.random() * Math.PI * 2;
  const angleSeparation = lerp(
    (2 * Math.PI) / 3, // 120 degrees
    (4 * Math.PI) / 3, // 240 degrees
    Math.random(),
  );
  const endAngle = startAngle + angleSeparation;

  const start = pointOnCircle(cx, cy, radius, startAngle);
  const end = pointOnCircle(cx, cy, radius, endAngle);

  // 2. Generate 2-4 interior waypoints that zigzag across the chord
  //    This creates the swooshy S-curve character.
  const numInterior = 2 + Math.floor(Math.random() * 3); // 2, 3, or 4

  const waypoints: Point2D[] = [start];

  // Direction perpendicular to the start→end chord
  const chordAngle = Math.atan2(end.y - start.y, end.x - start.x);
  const perpAngle = chordAngle + Math.PI / 2;

  // ~30% chance to inject a loop: two consecutive waypoints on the SAME side
  // of the chord, creating a U-turn/loop instead of a pure S-curve
  const loopIndex = Math.random() < 0.3 && numInterior >= 3
    ? 1 + Math.floor(Math.random() * (numInterior - 1))
    : -1;

  for (let i = 0; i < numInterior; i++) {
    const t = (i + 1) / (numInterior + 1);

    // Base position along the chord
    const baseX = lerp(start.x, end.x, t);
    const baseY = lerp(start.y, end.y, t);

    // Alternating perpendicular displacement (zigzag creates S-curves).
    // If this is the loop index, repeat the previous sign to create a loop.
    let sign: number;
    if (i === loopIndex) {
      sign = (i - 1) % 2 === 0 ? 1 : -1; // Same side as previous = loop
    } else {
      sign = i % 2 === 0 ? 1 : -1;
    }
    const displacement = radius * lerp(0.35, 0.75, Math.random()) * sign;

    let wx = baseX + displacement * Math.cos(perpAngle);
    let wy = baseY + displacement * Math.sin(perpAngle);

    // Clamp waypoints to stay inside the circle (92% radius)
    const dx = wx - cx;
    const dy = wy - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > radius * 0.92) {
      const scale = (radius * 0.92) / dist;
      wx = cx + dx * scale;
      wy = cy + dy * scale;
    }

    waypoints.push({ x: wx, y: wy });
  }

  waypoints.push(end);

  // 3. Build cubic bezier segments with C1 continuity
  const segments = buildBezierSegments(waypoints, radius, cx, cy);

  // 4. Sample the curve at high resolution
  const samplesPerSegment = Math.max(25, Math.floor(120 / segments.length));
  const points = sampleCurve(segments, samplesPerSegment);

  // 5. Calculate total arc length
  let totalLength = 0;
  for (let i = 1; i < points.length; i++) {
    totalLength += distance(points[i - 1], points[i]);
  }

  return {
    points,
    segments,
    start,
    end,
    totalLength,
    circle: { cx, cy, radius },
  };
}

/**
 * Build cubic bezier segments through waypoints with C1 continuity.
 * Control handles create wide, sweeping arcs for dramatic fly line curves.
 */
function buildBezierSegments(
  waypoints: Point2D[],
  circleRadius: number,
  circleCx: number,
  circleCy: number,
): FlyCurveSegment[] {
  const segments: FlyCurveSegment[] = [];
  const n = waypoints.length;

  for (let i = 0; i < n - 1; i++) {
    const p0 = waypoints[i];
    const p3 = waypoints[i + 1];
    const chordLength = distance(p0, p3);

    // Handle length: 55-80% of chord — longer handles = wider arcs
    // Gradually decrease toward end (fly line settling)
    const progress = i / Math.max(1, n - 2);
    const handleRatio = lerp(0.80, 0.50, progress);
    const handleLength = chordLength * handleRatio;

    // Tangent direction at start of segment
    let tangentAngle: number;
    if (i === 0) {
      // First segment: tangent from start toward first interior point
      tangentAngle = Math.atan2(p3.y - p0.y, p3.x - p0.x);
      // Wide angular sweep for dramatic opening arc (±50 degrees)
      tangentAngle += (Math.random() - 0.5) * (Math.PI * 5 / 9);
    } else {
      // C1 continuity: mirror the previous segment's end tangent
      const prevSeg = segments[i - 1];
      tangentAngle = Math.atan2(
        prevSeg.p3.y - prevSeg.cp2.y,
        prevSeg.p3.x - prevSeg.cp2.x,
      );
    }

    // Control point 1: extend from p0 along tangent
    const cp1: Point2D = {
      x: p0.x + handleLength * Math.cos(tangentAngle),
      y: p0.y + handleLength * Math.sin(tangentAngle),
    };

    // Control point 2: aim back from p3 toward p0 with perpendicular offset
    // The alternating offset creates the dramatic S-curve swoops
    let endTangentAngle = Math.atan2(p0.y - p3.y, p0.x - p3.x);
    const offsetSign = i % 2 === 0 ? 1 : -1;
    endTangentAngle += offsetSign * lerp(0.5, 1.0, Math.random()) * (Math.PI / 3);

    const cp2: Point2D = {
      x: p3.x + handleLength * Math.cos(endTangentAngle),
      y: p3.y + handleLength * Math.sin(endTangentAngle),
    };

    // Clamp control points to stay within circle (with 10% padding)
    // Use actual circle center — NOT midpoint of start/end
    const maxR = circleRadius * 1.1;

    segments.push({
      p0,
      cp1: clampPointToCircle(cp1, circleCx, circleCy, maxR),
      cp2: clampPointToCircle(cp2, circleCx, circleCy, maxR),
      p3,
    });
  }

  return segments;
}

/**
 * Clamp a point so it stays within a circle of given radius around center
 */
function clampPointToCircle(point: Point2D, cx: number, cy: number, maxRadius: number): Point2D {
  const dx = point.x - cx;
  const dy = point.y - cy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist <= maxRadius) return point;
  const scale = maxRadius / dist;
  return { x: cx + dx * scale, y: cy + dy * scale };
}


/**
 * Sample a composite bezier curve at high resolution
 */
function sampleCurve(segments: FlyCurveSegment[], samplesPerSegment: number): Point2D[] {
  const points: Point2D[] = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const startT = i === 0 ? 0 : 1; // Skip first point of subsequent segments (shared with previous end)
    const numSamples = samplesPerSegment;

    for (let j = startT; j <= numSamples; j++) {
      const t = j / numSamples;
      points.push(evalCubicBezier(seg.p0, seg.cp1, seg.cp2, seg.p3, t));
    }
  }

  return points;
}

// ============================================================
// SVG path generation
// ============================================================

/**
 * Convert FlyCurve segments to an SVG path d-attribute string
 */
export function flyCurveToSvgPath(curve: FlyCurve): string {
  if (curve.segments.length === 0) return '';

  const { segments } = curve;
  let d = `M ${segments[0].p0.x.toFixed(2)} ${segments[0].p0.y.toFixed(2)}`;

  for (const seg of segments) {
    d += ` C ${seg.cp1.x.toFixed(2)} ${seg.cp1.y.toFixed(2)}, ${seg.cp2.x.toFixed(2)} ${seg.cp2.y.toFixed(2)}, ${seg.p3.x.toFixed(2)} ${seg.p3.y.toFixed(2)}`;
  }

  return d;
}

// ============================================================
// Trace matching utilities
// ============================================================

/**
 * Find the index of the nearest point on the curve to the given position.
 * Returns the index into curve.points.
 */
export function findNearestPointIndex(
  curve: FlyCurve,
  pos: Point2D,
  searchStartIndex: number = 0,
): number {
  let bestIndex = searchStartIndex;
  let bestDistSq = Infinity;

  // Search from the current progress forward (and a bit behind for tolerance)
  const lookBehind = Math.max(0, searchStartIndex - 10);
  const lookAhead = Math.min(curve.points.length, searchStartIndex + curve.points.length);

  for (let i = lookBehind; i < lookAhead; i++) {
    const idx = i % curve.points.length;
    if (idx >= curve.points.length) break;
    const p = curve.points[idx];
    const dx = pos.x - p.x;
    const dy = pos.y - p.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestIndex = idx;
    }
  }

  return bestIndex;
}

/**
 * Calculate the distance from a point to the nearest point on the curve
 */
export function distanceToCurve(curve: FlyCurve, pos: Point2D, searchStartIndex: number = 0): number {
  const nearestIdx = findNearestPointIndex(curve, pos, searchStartIndex);
  return distance(pos, curve.points[nearestIdx]);
}

/**
 * Calculate the progress (0-1) along the curve at a given point index
 */
export function curveProgressAtIndex(curve: FlyCurve, index: number): number {
  if (curve.points.length <= 1) return 0;
  return index / (curve.points.length - 1);
}

// Re-export distance for use in other modules
export { distance, lerpPoint };
